import { getWrapperCellPositions } from "@/components/suggestions/providers/utils/getTableCellFormula";
import {
  getComputedResultNode,
  createDependencyMap,
  getTransitiveDeps,
} from "@/models/computedNode/utils";
import { isEqualsSign } from "@/models/utils/semanticChecks";

import type { SingleSuggestion, SuggestionProvider } from "@/components/suggestions/types";
import type { NodeJson } from "@corca/data";

export const computedSuggestionsProvider: SuggestionProvider = {
  type: "computed",
  search: (str, doc) => {
    // Match "ref" prefix
    if (str.length === 0) return [];
    if (!"reference".startsWith(str.toLowerCase()) && str !== "#" && !/^R\d*$/.test(str)) {
      return [];
    }

    const computedWrappers = doc.textNode.wrapperNodes.filter((w) => w.computed && w.refId);
    if (computedWrappers.length === 0) return [];

    const currentWrapper = doc.cursor.parentWrapperNode;

    const deps = createDependencyMap(doc);

    const { tiptap } = doc.editor;
    // A computed equation living in a table cell is known by its address, not its formula.
    const cellPositions = tiptap ? getWrapperCellPositions(tiptap) : undefined;

    const suggestions: SingleSuggestion[] = computedWrappers
      .map((wrapper) => {
        if (currentWrapper && wrapper.id === currentWrapper.id) return null;

        let refLabel: string | undefined;
        if (/^R\d+$/.test(str)) {
          const ref = doc.computedNodes.getComputedRefIndex(wrapper.refId!);
          refLabel = ref == null ? undefined : `R${ref + 1}`;
          if (!refLabel?.startsWith(str)) return null;
        }

        // Skip targets that already transitively depend on the current
        // wrapper — inserting a reference would create a cycle.
        if (currentWrapper) {
          const transitiveDeps = getTransitiveDeps(wrapper.id, deps);
          if (transitiveDeps.has(currentWrapper.id)) return null;
        }

        const inlineNode = wrapper.nodes.find((n) => n.type === "inline");
        if (!inlineNode) return null;

        const allChildren = [...inlineNode.nodes];
        const firstEqualsIdx = allChildren.findIndex(isEqualsSign);
        const resultNode = getComputedResultNode(wrapper);
        const looseRhs = firstEqualsIdx >= 0 ? allChildren.slice(firstEqualsIdx + 1) : allChildren;
        const rhsNodes = resultNode ? (resultNode.inlineNode?.nodes ?? []) : looseRhs;

        const state = doc.computedNodes.getState(wrapper);
        const invalid = state === "invalid" || state === "error";

        const referenceNodeJson: NodeJson = {
          type: "computedReference",
          props: {
            targetRefId: wrapper.refId!,
            ...(invalid ? { invalid: true } : {}),
          },
          children: [
            {
              type: "inline",
              props: {},
              children: rhsNodes.map((n) => n.toJSON()),
            },
          ],
        };

        let priority = 1;
        if (str === "#" || /^R\d*$/.test(str)) priority = 2;
        // The label typed in full outranks the longer ones its prefix also matches (R2 over R20).
        if (refLabel === str) priority = 3;
        if (state !== "valid") priority = 0;

        const position = cellPositions?.get(wrapper.id);

        return {
          type: "single",
          item: {
            category: position?.address,
            type: "expression",
            symbol: "Reference",
            displayName: "Reference",
            nodesJson: allChildren.map((n) => n.toJSON()),
          },
          action: {
            type: "insert-expression",
            nodesJson: [referenceNodeJson],
          },
          priority,
          canBeAutocompleted: false,
        } as SingleSuggestion;
      })
      .filter(Boolean);

    return [suggestions];
  },
};
